import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { ProvidersAPI } from '../api';

interface ExternalServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: {
    name: string;
    type?: string;
    domain?: string;
    price: number;
    payType: 'ANUAL' | 'MENSUAL';
    startDate: string;
    nextPaymentDate?: string;
  }) => void;
  initialData?: {
    name: string;
    type?: string;
    domain?: string;
    price: number;
    payType: 'ANUAL' | 'MENSUAL';
    startDate: string | Date;
    nextPaymentDate?: string | Date;
  };
}

export function ExternalServiceModal({ isOpen, onClose, onConfirm, initialData }: ExternalServiceModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    domain: '',
    price: '',
    payType: 'ANUAL' as 'ANUAL' | 'MENSUAL',
    // Interval solo se usa para ANUAL y representa "cada cuántos años"
    annualInterval: '1',
    startDate: new Date().toISOString().split('T')[0],
    nextPaymentDate: '',
  });

  const [providers, setProviders] = useState<string[]>([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [providersError, setProvidersError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    async function fetchProviders() {
      try {
        setLoadingProviders(true);
        const res = await ProvidersAPI.list();
        if (res.success && res.data) {
          const names = (res.data as any[]).map((p) => p.name as string).filter(Boolean);
          setProviders(names);
          setProvidersError(null);
        } else {
          setProvidersError(res.error || 'No se pudieron cargar los proveedores');
        }
      } catch (e) {
        console.error(e);
        setProvidersError('Error al cargar los proveedores');
      } finally {
        setLoadingProviders(false);
      }
    }
    fetchProviders();
  }, [isOpen]);

  useEffect(() => {
    if (initialData) {
      const toDateString = (date: string | Date) => {
        if (!date) return '';
        if (typeof date === 'string') return date.split('T')[0];
        try {
            return date.toISOString().split('T')[0];
        } catch (e) {
            console.error('Invalid date format:', date);
            return '';
        }
      };

      // Calcular intervalo anual en base a startDate y nextPaymentDate, si existen
      let annualInterval = '1';
      if (initialData.startDate && initialData.nextPaymentDate) {
        try {
          const start = new Date(initialData.startDate);
          const next = new Date(initialData.nextPaymentDate);

          const sameMonthAndDay =
            start.getMonth() === next.getMonth() && start.getDate() === next.getDate();
          const diffYears = next.getFullYear() - start.getFullYear();

          if (sameMonthAndDay && diffYears > 1) {
            annualInterval = String(diffYears);
          }
        } catch (e) {
          console.error('Error calculating annual interval from dates:', e);
        }
      }

      setFormData({
        name: initialData.name,
        type: initialData.type || '',
        domain: initialData.domain || '',
        price: initialData.price.toString(),
        payType: initialData.payType,
        annualInterval,
        startDate: toDateString(initialData.startDate),
        nextPaymentDate: initialData.nextPaymentDate ? toDateString(initialData.nextPaymentDate) : '',
      });
    } else {
        // Reset to default when opening fresh
        setFormData({
          name: '',
          type: '',
          domain: '',
          price: '',
          payType: 'ANUAL' as 'ANUAL' | 'MENSUAL',
          annualInterval: '1',
          startDate: new Date().toISOString().split('T')[0],
          nextPaymentDate: '',
        });
    }
  }, [initialData, isOpen]);

  useEffect(() => {
    // Only auto-calculate if NOT in edit mode (no initialData) OR if user changes start date/pay type manually
    // But simplistic approach: auto-calc always based on current inputs is safer for consistency
    if (formData.startDate && formData.payType) {
      const parts = formData.startDate.split('-');
      // Crear fecha usando componentes locales para evitar problemas de zona horaria
      const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Si la fecha de inicio es futura, ese es el próximo pago
      if (date > today) {
        setFormData(prev => ({
          ...prev,
          nextPaymentDate: formData.startDate
        }));
        return;
      }

      // Si es pasada, calcular el próximo vencimiento futuro
      if (formData.payType === 'ANUAL') {
        const startDay = Number(parts[2]);
        const startMonth = Number(parts[1]) - 1;
        const interval = Math.max(1, Number(formData.annualInterval) || 1);

        // Empezamos desde la fecha de inicio original
        let candidate = new Date(Number(parts[0]), startMonth, startDay);

        // Avanzar de a "interval" años hasta que quede en el futuro
        while (candidate <= today) {
          // Sumar años manteniendo mes y día cuando sea posible
          const targetYear = candidate.getFullYear() + interval;
          candidate = new Date(targetYear, startMonth, 1);

          const daysInTargetMonth = new Date(targetYear, startMonth + 1, 0).getDate();
          if (startDay > daysInTargetMonth) {
            candidate.setDate(daysInTargetMonth);
          } else {
            candidate.setDate(startDay);
          }
        }

        date.setTime(candidate.getTime());
      } else {
        // Mensual: Queremos el próximo mes futuro
        // Algoritmo simple: mientras sea <= hoy, sumar 1 mes
        const startDay = Number(parts[2]);
        
        while (date <= today) {
           // Sumar un mes manteniendo el día original si es posible
           // Primero seteamos al día 1 para evitar saltos raros al sumar meses
           date.setDate(1);
           date.setMonth(date.getMonth() + 1);
           
           // Intentar poner el día original
           const year = date.getFullYear();
           const month = date.getMonth();
           const daysInMonth = new Date(year, month + 1, 0).getDate();
           
           if (startDay > daysInMonth) {
             date.setDate(daysInMonth);
           } else {
             date.setDate(startDay);
           }
        }
      }

      // Formatear a YYYY-MM-DD
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      setFormData(prev => ({
        ...prev,
        nextPaymentDate: `${year}-${month}-${day}`
      }));
    }
  }, [formData.startDate, formData.payType, formData.annualInterval]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm({
      name: formData.name,
      type: formData.type || undefined,
      domain: formData.domain || undefined,
      price: Number(formData.price),
      payType: formData.payType,
      startDate: formData.startDate,
      nextPaymentDate: formData.nextPaymentDate || undefined,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden transform transition-all" 
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-6 border-b border-gray-100 dark:border-slate-700">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
            {initialData ? 'Editar Servicio Externo' : 'Nuevo Servicio Externo'}
          </h3>
          <button 
            onClick={onClose} 
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors dark:hover:bg-slate-700 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                Nombre del Servicio
              </label>
              {providers.length > 0 && !providersError ? (
                <div className="relative">
                  <select
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all appearance-none dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:focus:bg-slate-600"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  >
                    <option value="">Selecciona un proveedor...</option>
                    {(formData.name && !providers.includes(formData.name)
                      ? [formData.name, ...providers]
                      : providers
                    ).map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500">
                    <svg className="h-4 w-4 fill-current" viewBox="0 0 20 20">
                      <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                    </svg>
                  </div>
                </div>
              ) : (
                <input
                  type="text"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:focus:bg-slate-600"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej. Hostgator"
                />
              )}
              {loadingProviders && (
                <p className="text-xs text-gray-400 dark:text-gray-500">Cargando proveedores...</p>
              )}
              {providersError && (
                <p className="text-xs text-red-500 dark:text-red-400">{providersError}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                Tipo de Servicio <span className="text-gray-400 font-normal">(Opcional)</span>
              </label>
              <input
                type="text"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:focus:bg-slate-600"
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                placeholder="Ej. Hosting, Streaming"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
              Dominio <span className="text-gray-400 font-normal">(Opcional)</span>
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:focus:bg-slate-600"
              value={formData.domain}
              onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
              placeholder="ejemplo.com"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                Precio
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                  <span className="text-gray-500 font-medium">$</span>
                </div>
                <input
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  className="w-full pl-8 pr-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:focus:bg-slate-600"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                Ciclo de Facturación
              </label>
              <div className="relative">
                <select
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all appearance-none dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:focus:bg-slate-600"
                  value={formData.payType}
                  onChange={(e) => setFormData({ ...formData, payType: e.target.value as 'ANUAL' | 'MENSUAL' })}
                >
                  <option value="ANUAL">Anual</option>
                  <option value="MENSUAL">Mensual</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500">
                  <svg className="h-4 w-4 fill-current" viewBox="0 0 20 20">
                    <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                  </svg>
                </div>
              </div>
              {formData.payType === 'ANUAL' && (
                <div className="mt-1 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                  <span>Cada</span>
                  <input
                    type="number"
                    min={1}
                    className="w-16 px-2 py-1 rounded-md border border-gray-200 bg-gray-50 focus:bg-white focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none transition-all dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:focus:bg-slate-600"
                    value={formData.annualInterval}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        annualInterval:
                          e.target.value === '' ? '' : String(Math.max(1, Number(e.target.value) || 1)),
                      })
                    }
                  />
                  <span>año(s)</span>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                Fecha de Inicio
              </label>
              <input
                type="date"
                required
                className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:focus:bg-slate-600"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                Próximo Pago
              </label>
              <input
                type="date"
                disabled
                className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed dark:bg-slate-800 dark:border-slate-600 dark:text-gray-500"
                value={formData.nextPaymentDate}
              />
            </div>
          </div>

          <div className="flex justify-end gap-4 pt-4 mt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-200 transition-colors dark:bg-slate-700 dark:text-gray-200 dark:border-slate-600 dark:hover:bg-slate-600"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-lg shadow-blue-600/20 transition-all hover:scale-[1.02]"
            >
              Guardar Servicio
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
